"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import "../styles/app.css"

import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
)

const Calendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
)

const Sparkles = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path>
    <path d="M5 3v4"></path>
    <path d="M19 17v4"></path>
    <path d="M3 5h4"></path>
    <path d="M17 19h4"></path>
  </svg>
)

const Heart = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
)

const ThumbsUp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 10v12"></path>
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path>
  </svg>
)

const ThumbsDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 14V2"></path>
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"></path>
  </svg>
)

const QuestionnaireProgressIndicator = ({ currentGroup, totalGroups, highestGroupReached, onStepClick }) => {
  const getProgressText = () => {
    switch (currentGroup) {
      case 1:
        return "We all have your favorites 🥒"
      case 2:
        return "How you watch says a lot ..."
      case 3:
        return "This info stays between us and the popcorn 🍿"
      default:
        return "Let's get started!"
    }
  }

  const groupLabels = ["Preferences", "Lifestyle", "Background"]

  return (
    <div className="progress-container">
      <div className="progress-content">
        <div className="progress-text">
          <span className="progress-title">{getProgressText()}</span>
        </div>

        <div className="step-indicators">
          {Array.from({ length: totalGroups }, (_, index) => {
            const stepNumber = index + 1
            const isCompleted = stepNumber < currentGroup
            const isCurrent = stepNumber === currentGroup
            const isReached = stepNumber <= highestGroupReached

            return (
              <div key={stepNumber} className="step-item">
                <div
                  onClick={() => onStepClick(stepNumber)}
                  className={`step-circle ${isReached ? "clickable" : "disabled"} ${
                    isCompleted || isCurrent ? "active" : "inactive"
                  } ${isCurrent ? "current" : ""}`}
                >
                  {stepNumber}
                </div>
                {stepNumber < totalGroups && (
                  <div className={`step-connector ${isCompleted ? "active" : "inactive"}`} />
                )}
              </div>
            )
          })}
        </div>

        <div className="step-labels">
          {groupLabels.map((label, index) => {
            const stepNumber = index + 1
            return (
              <div key={stepNumber} className="label-item">
                <div className="label-wrapper">
                  <span className={`step-label ${currentGroup >= stepNumber ? "active" : "inactive"}`}>{label}</span>
                </div>
                {stepNumber < totalGroups && <div className="label-spacer" />}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const MovieGenreSelectionForm = ({ formData, onGenreChange }) => {
  return (
    <>
      <div className="section-header">
        <h1 className="section-title">What kind of movies do you like?</h1>
        <p className="section-subtitle">Pick your favorite genres!</p>
      </div>

      {/* Add horizontal header row for button columns */}
      <div className="genre-header-row">
        <div className="genre-header-spacer" />
        <span className="genre-header-label">Very much a fan</span>
        <span className="genre-header-label">Somewhat a fan</span>
        <span className="genre-header-label">Not at all a fan</span>
      </div>

      <div className="genre-list">
        {[
          { id: "romance", label: "Romance", icon: "fa-solid fa-heart" },
          { id: "rated-r-comedy", label: "R-Rated Comedy", icon: "fa-solid fa-face-laugh-squint" },
          { id: "family", label: "Family", icon: "fa-solid fa-house-user" },
          { id: "superhero", label: "Superhero", icon: "fa-solid fa-mask" },
          { id: "comedy", label: "Comedy", icon: "fa-solid fa-face-grin-squint" },
          { id: "suspense-thriller", label: "Suspense / Thriller", icon: "fa-solid fa-mask-face" },
          { id: "horror", label: "Horror", icon: "fa-solid fa-ghost" },
          { id: "scifi-fantasy", label: "Sci-fi / Fantasy", icon: "fa-solid fa-rocket" },
          { id: "action", label: "Action", icon: "fa-solid fa-explosion" },
          { id: "animated", label: "Animated", icon: "fa-solid fa-palette" },
          { id: "drama", label: "Drama", icon: "fa-solid fa-masks-theater" },
        ].map((genre, index) => (
          <div key={genre.id}>
            <div className="genre-item">
              <label className="genre-label">
                <i className={genre.icon} style={{ marginRight: '8px', color: '#009932' }}></i>
                {genre.label}
              </label>
              <div className="genre-options">
                {[
                  { value: "love", icon: <Heart />, color: "green" },
                  { value: "like", icon: <ThumbsUp />, color: "light-green" },
                  { value: "dislike", icon: <ThumbsDown />, color: "red" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => onGenreChange(genre.id, option.value)}
                    className={`genre-option ${formData.genres[genre.id] === option.value ? `selected ${option.color}` : ""}`}
                  >
                    {/* Only show the icon, no text */}
                    <div className="option-content">
                      {option.icon}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {index < 10 && <div className="divider" />}
          </div>
        ))}
      </div>

      <div className="selection-counter">
        <span className="counter-text">Select at least 3 genres to continue</span>
        <span className="counter-number">{Object.keys(formData.genres).length}/3 selected</span>
      </div>
    </>
  )
}

const ViewingHabitsForm = ({ formData, onStreamingServiceChange }) => {
  return (
    <>
      <div className="section-header">
        <h1 className="section-title">Tell us about your viewing habits</h1>
      </div>

      <div className="form-section">
        <div className="question-group">
          <label className="question-label">Which services do you typically watch each week?</label>
          <div className="service-list">
            {[
              "Netflix",
              "Hulu",
              "Disney+",
              "HBO Max",
              "Apple TV",
              "Amazon Prime Video",
              "Adult Swim",
              "BET",
              "Roku",
              "BBC",
              "CNN",
              "Fire TV",
              "Oxygen / OWN / WEtv / UPtv",
            ].map((service, index) => (
              <div key={service}>
                <div className="service-item">
                  <label className="service-label">{service}</label>
                  <div className="service-options">
                    {[
                      { value: true, label: "Yes", color: "green" },
                      { value: false, label: "No", color: "red" },
                    ].map((option) => (
                      <button
                        key={option.label}
                        onClick={() => onStreamingServiceChange(service, option.value)}
                        className={`service-option ${formData.streamingServices[service] === option.value ? `selected ${option.color}` : ""}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {index < 12 && <div className="divider" />}
              </div>
            ))}
          </div>
          <div className="selection-counter">
            <span className="counter-text">Select at least 1 service to continue</span>
            <span className="counter-number">
              {Object.values(formData.streamingServices).filter(Boolean).length} selected
            </span>
          </div>
        </div>
      </div>
    </>
  )
}

const DemographicInfoForm = ({ formData, onChildrenChange, onAreaTypeChange, onReligiosityChange, onCollegeDegreeChange }) => {
  return (
    <>
      <div className="section-header">
        <h1 className="section-title">Just a little more about you</h1>
        <p className="section-subtitle">These questions help us narrow down your recommendations!</p>
      </div>

      <div className="form-section">
        <div className="question-group">
          <label className="question-label">How many children do you have?</label>
          <div className="radio-grid children-grid">
            {[
              { value: "none", label: "None" },
              { value: "one", label: "One" },
              { value: "two_or_more", label: "Two or more" },
            ].map((option) => (
              <label
                key={option.value}
                className={`radio-option ${formData.children === option.value ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="children"
                  value={option.value}
                  checked={formData.children === option.value}
                  onChange={(e) => onChildrenChange(e.target.value)}
                  className="radio-input"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        <div className="question-group">
          <label className="question-label">What type of area do you live in?</label>
          <div className="radio-grid area-grid">
            {[
              { value: "urban", label: "Urban" },
              { value: "suburban", label: "Suburban" },
              { value: "rural", label: "Rural" },
              { value: "other", label: "Other" },
            ].map((option) => (
              <label
                key={option.value}
                className={`radio-option ${formData.areaType === option.value ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="areaType"
                  value={option.value}
                  checked={formData.areaType === option.value}
                  onChange={(e) => onAreaTypeChange(e.target.value)}
                  className="radio-input"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        <div className="question-group">
          <label className="question-label">How religious are you?</label>
          <div className="radio-grid religion-grid">
            {[
              { value: "very_religious", label: "Very religious" },
              { value: "somewhat_religious", label: "Somewhat religious" },
              { value: "not_at_all_religious", label: "Not at all religious" },
            ].map((option) => (
              <label
                key={option.value}
                className={`radio-option ${formData.religiosity === option.value ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="religiosity"
                  value={option.value}
                  checked={formData.religiosity === option.value}
                  onChange={(e) => onReligiosityChange(e.target.value)}
                  className="radio-input"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        <div className="question-group">
          <label className="question-label">Do you have a college degree?</label>
          <div className="radio-grid degree-grid">
            {[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ].map((option) => (
              <label
                key={option.value}
                className={`radio-option ${formData.hasCollegeDegree === option.value ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="degree"
                  value={option.value}
                  checked={formData.hasCollegeDegree === option.value}
                  onChange={(e) => onCollegeDegreeChange(e.target.value)}
                  className="radio-input"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

const NavigationButtons = ({ currentGroup, totalGroups, isGroupComplete, onContinue, onGoHome, isSubmitting }) => {
  return (
    <div className="action-buttons">
      {currentGroup === 3 ? (
        <button
          onClick={onGoHome}
          disabled={!isGroupComplete || isSubmitting}
          className={`btn btn-full ${!isGroupComplete || isSubmitting ? "disabled" : "btn-primary"}`}
        >
          {isSubmitting ? "Saving..." : "Finished"}
        </button>
      ) : (
        <div className="button-group">
          <button
            onClick={onContinue}
            disabled={!isGroupComplete || isSubmitting}
            className={`btn btn-flex ${!isGroupComplete || isSubmitting ? "disabled" : "btn-primary"}`}
          >
            Continue <ChevronRight />
          </button>
          <button onClick={onGoHome} disabled={isSubmitting} className="btn btn-flex btn-outline">
            {isSubmitting ? "Saving..." : "Back to Home"}
          </button>
        </div>
      )}
    </div>
  )
}

// Helper function to get CSRF token (copied from onboarding.jsx)
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Function to map frontend formData to backend's expected {name: "qX", value: "Y"} format
const mapQuestionnaireDataToBackend = (formData) => {
  const data = [];

  // Genre Preferences (q3r1 to q3r11)
  const genreMapping = {
    "family": 1,
    "animated": 2,
    "horror": 3,
    "romance": 4,
    "scifi-fantasy": 5,
    "suspense-thriller": 6,
    "comedy": 7,
    "rated-r-comedy": 8,
    "superhero": 9,
    "action": 10,
    "drama": 11,
  };
  const preferenceMapping = {
    "love": 1,
    "like": 2,
    "dislike": 3,
  };

  for (const genreKey in formData.genres) {
    const qId = genreMapping[genreKey];
    const value = preferenceMapping[formData.genres[genreKey]];
    if (qId && value) {
      data.push({ name: `q3r${qId}`, value: value.toString() });
    }
  }

  // Viewing Habits (q4 for streaming services)
  const streamingServiceMapping = {
    "CNN": 1,
    "BBC": 2,
    "Oxygen / OWN / WEtv / UPtv": 3, // This maps to q4r3 in original questionnaire
    "BET": 4,
    "Adult Swim": 5,
    "Hulu": 6,
    "Netflix": 7,
    "Disney+": 8,
    "HBO Max": 9,
    "Amazon Prime Video": 10,
    "Roku": 11,
    "Fire TV": 12, // This maps to q4r12 for Amazon Fire TV in original
    "Apple TV": 13,
  };
  for (const serviceName in formData.streamingServices) {
    const qId = streamingServiceMapping[serviceName];
    if (qId && formData.streamingServices[serviceName] !== undefined) {
      // Backend expects 1 for 'Yes', 0 for 'No'
      data.push({ name: `q4r${qId}`, value: formData.streamingServices[serviceName] ? "1" : "0" });
    }
  }

  // Demographic Info (q7, q8, q5, q9)
  if (formData.children) {
    const childrenMapping = {"none": 1, "one": 2, "two_or_more": 3};
    data.push({ name: "q7", value: childrenMapping[formData.children].toString() });
  }
  if (formData.areaType) {
    const areaTypeMapping = {"urban": 1, "suburban": 2, "rural": 3, "other": 4};
    data.push({ name: "q8", value: areaTypeMapping[formData.areaType].toString() });
  }
  if (formData.religiosity) {
    const religiosityMapping = {"very_religious": 1, "somewhat_religious": 2, "not_at_all_religious": 3};
    data.push({ name: "q5", value: religiosityMapping[formData.religiosity].toString() });
  }
  if (formData.hasCollegeDegree) {
    data.push({ name: "q9", value: formData.hasCollegeDegree === "yes" ? "1" : "2" });
  }

  return data;
};

// Function to map backend data (qX, qXrY) to frontend's formData structure
const mapBackendDataToFrontend = (backendData) => {
  const formData = {
    genres: {},
    streamingServices: {},
    children: "",
    areaType: "",
    religiosity: "",
    hasCollegeDegree: "",
  };

  const reverseGenreMapping = {
    "1": "family", "2": "animated", "3": "horror", "4": "romance",
    "5": "scifi-fantasy", "6": "suspense-thriller", "7": "comedy",
    "8": "rated-r-comedy", "9": "superhero", "10": "action", "11": "drama",
  };
  const reversePreferenceMapping = {
    "1": "love", "2": "like", "3": "dislike",
  };

  const reverseStreamingServiceMapping = {
    "1": "CNN", "2": "BBC", "3": "Oxygen / OWN / WEtv / UPtv",
    "4": "BET", "5": "Adult Swim", "6": "Hulu",
    "7": "Netflix", "8": "Disney+", "9": "HBO Max",
    "10": "Amazon Prime Video", "11": "Roku", "12": "Fire TV",
    "13": "Apple TV",
  };

  const reverseChildrenMapping = {"1": "none", "2": "one", "3": "two_or_more"};
  const reverseAreaTypeMapping = {"1": "urban", "2": "suburban", "3": "rural", "4": "other"};
  const reverseReligiosityMapping = {"1": "very_religious", "2": "somewhat_religious", "3": "not_at_all_religious"};

  backendData.forEach(item => {
    const { question, answer } = item;

    // Genres (q3rX)
    if (question.startsWith('q3r')) {
      const genreId = question.replace('q3r', '');
      const genreKey = reverseGenreMapping[genreId];
      const preference = reversePreferenceMapping[answer];
      if (genreKey && preference) {
        formData.genres[genreKey] = preference;
      }
    }
    // Viewing Habits (q4rX for streaming services)
    else if (question.startsWith('q4r')) {
      const serviceId = question.replace('q4r', '');
      const serviceName = reverseStreamingServiceMapping[serviceId];
      if (serviceName) {
        formData.streamingServices[serviceName] = answer === "1"; // 1 for true, 0 for false
      }
    }
    // Demographic Info (q7, q8, q5, q9)
    else if (question === 'q7') {
      formData.children = reverseChildrenMapping[answer];
    }
    else if (question === 'q8') {
      formData.areaType = reverseAreaTypeMapping[answer];
    }
    else if (question === 'q5') {
      formData.religiosity = reverseReligiosityMapping[answer];
    }
    else if (question === 'q9') {
      formData.hasCollegeDegree = answer === "1" ? "yes" : "no";
    }
  });

  return formData;
};

export default function PicklQuestionnaire() {
  const navigate = useNavigate();
  const [currentGroup, setCurrentGroup] = useState(1)
  const [highestGroupReached, setHighestGroupReached] = useState(1)
  const [formData, setFormData] = useState({
    genres: {},
    streamingServices: {},
    children: "",
    areaType: "",
    religiosity: "",
    hasCollegeDegree: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // New loading state
  const mainContentRef = useRef(null); // Add ref for scrolling
  const progressRef = useRef(null); // Add ref for progress indicator

  const totalGroups = 3;

  // useEffect to load existing data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/user-questionnaire-data/', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        const result = await response.json();

        if (result.Code === 111 && result.Data) {
          console.log("Loaded data from backend:", result.Data);
          const loadedFormData = mapBackendDataToFrontend(result.Data);
          setFormData(loadedFormData);
        } else {
          console.error("Failed to load user data:", result.Message);
          // Optionally, set an error state to display to the user
        }
      } catch (err) {
        console.error("Network error loading user data:", err);
        // Optionally, set an error state to display to the user
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []); // Empty dependency array means this runs once on mount

  // Handle form submission to backend
  const handleSubmit = async (redirectHome = true) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const submitData = mapQuestionnaireDataToBackend(formData);

      // If there's no data to submit, just redirect if needed
      if (submitData.length === 0 && redirectHome) {
        console.log("No data to submit, redirecting home.");
        navigate('/home');
        return; // Exit the function early
      }
      // Log the data being sent for debugging
      console.log("Submitting data:", submitData);

      const response = await fetch('/submitAns/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ data: JSON.stringify(submitData) })
      });

      const result = await response.json();

      if (result.Code === 111) {
        console.log("Submission successful!");
        if (redirectHome) {
          navigate('/home');
        }
      } else {
        setError(result.Message || 'Something went wrong during submission.');
        console.error("Submission error:", result.Message || "Unknown error");
      }
    } catch (err) {
      setError('Failed to submit data. Please check your network connection.');
      console.error('Submission network error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modified handleGoHome to submit data
  const handleGoHome = async () => {
    await handleSubmit(true); // Submit and then redirect
  };

  // Modified handleContinue to submit data if moving to next group, but not redirect
  const handleContinue = async () => {
    if (currentGroup < totalGroups) {
      await handleSubmit(false); // Submit but do not redirect
      setCurrentGroup(currentGroup + 1);
      setHighestGroupReached(Math.max(highestGroupReached, currentGroup + 1));
      
      // Scroll to top using multiple approaches
      setTimeout(() => {
        // Try scrolling to progress indicator first
        if (progressRef.current) {
          progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Also try window scroll as fallback
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  // Scroll to top when currentGroup changes
  useEffect(() => {
    // Only scroll if we're not on the first load
    if (currentGroup > 1) {
      setTimeout(() => {
        // Try scrolling to progress indicator first
        if (progressRef.current) {
          progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Also try window scroll as fallback
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [currentGroup]);

  const handleStepClick = async (stepNumber) => {
    if (stepNumber <= highestGroupReached) {
      await handleSubmit(false); // Submit but do not redirect
      setCurrentGroup(stepNumber);
    }
  };

  const handleGenreChange = (genre, preference) => {
    setFormData((prev) => ({
      ...prev,
      genres: {
        ...prev.genres,
        [genre]: preference,
      },
    }))
  }

  const handleStreamingServiceChange = (service, selected) => {
    setFormData((prev) => ({
      ...prev,
      streamingServices: {
        ...prev.streamingServices,
        [service]: selected,
      },
    }))
  }

  const handleChildrenChange = (value) => {
    setFormData((prev) => ({ ...prev, children: value }))
  }

  const handleAreaTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, areaType: value }))
  }

  const handleReligiosityChange = (value) => {
    setFormData((prev) => ({ ...prev, religiosity: value }))
  }

  const handleCollegeDegreeChange = (value) => {
    setFormData((prev) => ({ ...prev, hasCollegeDegree: value }))
  }

  const isGroupComplete =
    currentGroup === 1
      ? Object.keys(formData.genres).length >= 3
      : currentGroup === 2
        ? Object.keys(formData.streamingServices).length >= 1
        : currentGroup === 3
          ? formData.children && formData.areaType && formData.religiosity && formData.hasCollegeDegree
          : false

  return (
    <div className="app-container">
      <Header />

      <div ref={progressRef}>
        <QuestionnaireProgressIndicator
          currentGroup={currentGroup}
          totalGroups={totalGroups}
          highestGroupReached={highestGroupReached}
          onStepClick={handleStepClick}
        />
      </div>

      <div className="main-content" ref={mainContentRef}>
        {error && (
          <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {currentGroup === 1 && <MovieGenreSelectionForm formData={formData} onGenreChange={handleGenreChange} />}

        {currentGroup === 2 && (
          <ViewingHabitsForm
            formData={formData}
            onStreamingServiceChange={handleStreamingServiceChange}
          />
        )}

        {currentGroup === 3 && (
          <DemographicInfoForm
            formData={formData}
            onChildrenChange={handleChildrenChange}
            onAreaTypeChange={handleAreaTypeChange}
            onReligiosityChange={handleReligiosityChange}
            onCollegeDegreeChange={handleCollegeDegreeChange}
          />
        )}

        <NavigationButtons
          currentGroup={currentGroup}
          totalGroups={totalGroups}
          isGroupComplete={isGroupComplete}
          onContinue={handleContinue}
          onGoHome={handleGoHome}
          isSubmitting={isSubmitting}
        />
      </div>

      <Footer />
    </div>
  )
}
